/**
 * Gate 2 — previous execution runs. Live execute/score stays on Gate2ExecutionPage.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, Chip, CircularProgress, Typography } from "@mui/material";
import { ReactComponent as BackIcon } from "../../asserts/logo/backicon.svg";
import IntakeGateTabsBar from "../../components/intake/IntakeGateTabsBar";
import { getExecutionRunResults, listExecutionRuns } from "../../services/gate2ExecutionClient";
import {
  CARD_SX,
  LABEL_SX,
  OUTLINED_BTN_SX,
  SECTION_SUBTITLE_SX,
  SECTION_TITLE_SX,
} from "./gate2Tokens";

const ACTIVE_GATE = 2;

function formatWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function statusChip(status) {
  const key = String(status || "running").toLowerCase();
  if (key === "completed") return { label: "Completed", bgcolor: "#DCFCE7", color: "#15803D" };
  if (key === "failed") return { label: "Failed", bgcolor: "#FEE2E2", color: "#B91C1C" };
  return { label: "Running", bgcolor: "#DBEAFE", color: "#005BF0" };
}

function verdictLabel(score) {
  if (score?.passed === true) return "Pass";
  if (score?.passed === false) return "Fail";
  return "Not scored";
}

export default function Gate2ExecutionRunsPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError("");
    try {
      const res = await listExecutionRuns(projectId, 50);
      if (!res?.success) {
        setError(res?.error || "Failed to load previous executions.");
        return;
      }
      setRuns(res.data?.runs || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Failed to load previous executions.");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const openRun = async (runId) => {
    setSelectedId(runId);
    setDetailLoading(true);
    setDetail(null);
    try {
      const res = await getExecutionRunResults(projectId, runId);
      if (!res?.success) {
        setError(res?.error || "Failed to load this execution.");
        return;
      }
      setDetail(res.data || null);
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Failed to load this execution.");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleGateClick = (gateIndex) => {
    if (gateIndex === 0) navigate(`/chat/${projectId}`);
    if (gateIndex === 1) navigate(`/gate1/${projectId}`);
    if (gateIndex === 2) navigate(`/gate2/${projectId}`);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: "#F5F5F5", minWidth: 0 }}>
      <IntakeGateTabsBar
        activeGate={ACTIVE_GATE}
        unlockedUpTo={ACTIVE_GATE}
        onGateClick={handleGateClick}
        onBack={() => navigate(`/gate2/${projectId}`)}
        onViewSummary={() => navigate(`/summary/${projectId}`)}
        summaryDisabled={false}
      />

      <Box sx={{ bgcolor: "#FFFFFF", borderBottom: "1px solid #E6E6E6", flexShrink: 0, px: { xs: 2, md: 3 }, py: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <Box
            component="button"
            type="button"
            onClick={() => navigate(`/gate2/${projectId}`)}
            aria-label="Back to execution"
            sx={{
              mt: 0.25,
              width: 32,
              height: 32,
              p: 0,
              border: "none",
              bgcolor: "transparent",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "4px",
              flexShrink: 0,
              "&:hover": { bgcolor: "#005BF014" },
            }}
          >
            <BackIcon style={{ width: 24, height: 24, display: "block" }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={SECTION_TITLE_SX}>Previous executions</Typography>
            <Typography sx={SECTION_SUBTITLE_SX}>
              Started and finished times for past Gate 2 runs. Live execution is unchanged.
            </Typography>
          </Box>
        </Box>
      </Box>

      {error ? (
        <Typography sx={{ px: { xs: 2, md: 3 }, pt: 2, fontSize: 13, color: "#DE350B", fontWeight: 600 }}>
          {error}
        </Typography>
      ) : null}

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : !runs.length ? (
          <Box sx={{ ...CARD_SX, p: 3 }}>
            <Typography sx={{ fontFamily: "Lato, sans-serif", color: "#555555" }}>
              No previous executions yet. Confirm scope and run execution first.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {runs.map((run) => {
              const chip = statusChip(run.status);
              const selected = selectedId === run.id;
              return (
                <Box key={run.id} sx={{ ...CARD_SX, p: 2.5 }}>
                  <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2 }}>
                    <Chip
                      label={chip.label}
                      size="small"
                      sx={{
                        fontFamily: "Lato, sans-serif",
                        fontWeight: 700,
                        bgcolor: chip.bgcolor,
                        color: chip.color,
                      }}
                    />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={LABEL_SX}>Started</Typography>
                      <Typography sx={{ fontFamily: "Lato, sans-serif", fontSize: 14, color: "#0F172A" }}>
                        {formatWhen(run.startedAt)}
                      </Typography>
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={LABEL_SX}>Finished</Typography>
                      <Typography sx={{ fontFamily: "Lato, sans-serif", fontSize: 14, color: "#0F172A" }}>
                        {formatWhen(run.finishedAt)}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontFamily: "Lato, sans-serif", fontSize: 13, color: "#64748B" }}>
                      {run.resultCount || 0} cases
                    </Typography>
                    <Button
                      sx={OUTLINED_BTN_SX}
                      onClick={() => {
                        if (selectedId === run.id) {
                          setSelectedId(null);
                          setDetail(null);
                          return;
                        }
                        openRun(run.id);
                      }}
                    >
                      {selected ? "Hide" : "View"}
                    </Button>
                  </Box>
                  {(run.scores || []).length ? (
                    <Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {run.scores.map((score) => (
                        <Chip
                          key={score.id || `${score.metricId}-${score.suiteId}`}
                          size="small"
                          label={`${score.metricName || score.metricId}: ${
                            score.value == null ? "—" : Number(score.value).toFixed(2)
                          } (${verdictLabel(score)})`}
                          sx={{ fontFamily: "Lato, sans-serif" }}
                        />
                      ))}
                    </Box>
                  ) : null}
                  {selected && selectedId === run.id ? (
                    <Box sx={{ mt: 2, borderTop: "1px solid #E2E8F0", pt: 2 }}>
                      {detailLoading ? (
                        <CircularProgress size={22} />
                      ) : (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                          {(detail?.scores || []).map((score) => (
                            <Typography
                              key={score.id || score.metricId}
                              sx={{ fontFamily: "Lato, sans-serif", fontSize: 13, color: "#0F172A" }}
                            >
                              {score.metricName || score.metricId}:{" "}
                              {score.value == null ? "not scored" : Number(score.value).toFixed(3)} —{" "}
                              {verdictLabel(score)}
                              {score.error ? ` (${score.error})` : ""}
                            </Typography>
                          ))}
                          {(detail?.results || []).map((row) => (
                            <Box key={row.id || row.testCaseId} sx={{ bgcolor: "#F8FAFC", borderRadius: "8px", p: 1.5 }}>
                              <Typography sx={{ fontFamily: "Lato, sans-serif", fontWeight: 700, fontSize: 14 }}>
                                {row.testCaseName || row.testCaseId || "Test case"}
                              </Typography>
                              <Typography sx={{ ...LABEL_SX, mt: 1 }}>Prompt</Typography>
                              <Typography sx={{ fontFamily: "Lato, sans-serif", fontSize: 13, whiteSpace: "pre-wrap" }}>
                                {row.prompt || "—"}
                              </Typography>
                              <Typography sx={{ ...LABEL_SX, mt: 1 }}>Expected</Typography>
                              <Typography sx={{ fontFamily: "Lato, sans-serif", fontSize: 13, whiteSpace: "pre-wrap" }}>
                                {row.expected || "—"}
                              </Typography>
                              <Typography sx={{ ...LABEL_SX, mt: 1 }}>Captured output</Typography>
                              <Typography sx={{ fontFamily: "Lato, sans-serif", fontSize: 13, whiteSpace: "pre-wrap" }}>
                                {row.actualOutput || "—"}
                              </Typography>
                              <Typography sx={{ fontFamily: "Lato, sans-serif", fontSize: 12, color: "#64748B", mt: 1 }}>
                                Outcome: {row.outcome || "—"}
                                {row.error?.message ? ` — ${row.error.message}` : ""}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  ) : null}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
}
